import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { getErrorMessage } from "../lib/utils";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { ArrowDownToLine, Package, Truck, CalendarIcon, Plus, History, Pencil, Trash2, Download, FileSpreadsheet, File } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const StockEntryPage = () => {
  const { user, hasPermission } = useAuth();
  const isAdmin = user?.role === "admin";
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [expirationDate, setExpirationDate] = useState(null);
  const [expirationDateText, setExpirationDateText] = useState("");
  const [formData, setFormData] = useState({
    product_id: "",
    supplier_id: "",
    quantity: "",
    unit_cost: "",
    entry_date: new Date().toISOString().split("T")[0],
  });

  // Edit state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editExpirationDate, setEditExpirationDate] = useState(null);
  const [editExpirationDateText, setEditExpirationDateText] = useState("");
  const [editFormData, setEditFormData] = useState({
    product_id: "",
    supplier_id: "",
    quantity: "",
    unit_cost: "",
    entry_date: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, suppliersRes, entriesRes] = await Promise.all([
        api.get("/products"),
        api.get("/suppliers"),
        api.get("/stock-entries"),
      ]);
      setProducts(productsRes.data);
      setSuppliers(suppliersRes.data);
      setEntries(entriesRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!hasPermission("add_entry")) {
      toast.error("Você não tem permissão para registrar entradas");
      return;
    }

    if (!expirationDate) {
      toast.error("Selecione a data de validade");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/stock-entries", {
        ...formData,
        quantity: parseInt(formData.quantity),
        unit_cost: parseFloat(formData.unit_cost),
        expiration_date: expirationDate.toISOString(),
        entry_date: formData.entry_date || null,
      });
      toast.success("Entrada registrada com sucesso!");
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erro ao registrar entrada"));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      product_id: "",
      supplier_id: "",
      quantity: "",
      unit_cost: "",
      entry_date: new Date().toISOString().split("T")[0],
    });
    setExpirationDate(null);
    setExpirationDateText("");
  };

  // Handle date text input
  const handleDateTextChange = (text, setDate, setDateText) => {
    // Remove non-digits
    let digits = text.replace(/\D/g, '');
    
    // Limit to 8 digits
    digits = digits.slice(0, 8);
    
    // Format with slashes
    let formatted = '';
    if (digits.length > 0) {
      formatted = digits.slice(0, 2);
    }
    if (digits.length > 2) {
      formatted += '/' + digits.slice(2, 4);
    }
    if (digits.length > 4) {
      formatted += '/' + digits.slice(4, 8);
    }
    
    setDateText(formatted);
    
    // Parse complete date
    if (digits.length === 8) {
      const day = parseInt(digits.slice(0, 2));
      const month = parseInt(digits.slice(2, 4));
      const year = parseInt(digits.slice(4, 8));
      
      if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 2000) {
        const date = new Date(year, month - 1, day);
        if (!isNaN(date.getTime()) && date.getDate() === day) {
          setDate(date);
        }
      }
    }
  };

  // Handle calendar selection
  const handleCalendarSelect = (date, setDate, setDateText) => {
    setDate(date);
    if (date) {
      setDateText(format(date, "dd/MM/yyyy", { locale: ptBR }));
    } else {
      setDateText("");
    }
  };

  // Edit functions
  const openEditDialog = (entry) => {
    setEditingEntry(entry);
    setEditFormData({
      product_id: entry.product_id,
      supplier_id: entry.supplier_id,
      quantity: entry.quantity.toString(),
      unit_cost: entry.unit_cost.toString(),
      entry_date: entry.entry_date || entry.created_at?.split("T")[0] || "",
    });
    try {
      const date = new Date(entry.expiration_date);
      setEditExpirationDate(date);
      setEditExpirationDateText(format(date, "dd/MM/yyyy", { locale: ptBR }));
    } catch {
      setEditExpirationDate(null);
      setEditExpirationDateText("");
    }
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingEntry) return;

    try {
      await api.put(`/stock-entries/${editingEntry.id}`, {
        product_id: editFormData.product_id,
        supplier_id: editFormData.supplier_id,
        quantity: parseInt(editFormData.quantity),
        unit_cost: parseFloat(editFormData.unit_cost),
        expiration_date: editExpirationDate?.toISOString(),
        entry_date: editFormData.entry_date || null,
      });
      toast.success("Entrada atualizada com sucesso!");
      setEditDialogOpen(false);
      setEditingEntry(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erro ao atualizar entrada"));
    }
  };

  const handleDelete = async (entry) => {
    try {
      await api.delete(`/stock-entries/${entry.id}`);
      toast.success("Entrada excluída com sucesso!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir entrada");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    try {
      return format(new Date(dateStr), "dd/MM/yyyy HH:mm", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    if (entries.length === 0) {
      toast.error("Sem dados para exportar");
      return;
    }

    const worksheetData = entries.map(entry => ({
      "Produto": entry.product_name,
      "Fornecedor": entry.supplier_name,
      "Validade": formatDate(entry.expiration_date),
      "Quantidade": entry.quantity,
      "Custo Unitário": entry.unit_cost,
      "Custo Total": entry.quantity * entry.unit_cost,
      "Data de Entrada": formatDateTime(entry.created_at),
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Entradas");

    // Auto-adjust column widths
    worksheet["!cols"] = [
      { wch: 25 }, { wch: 20 }, { wch: 12 },
      { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 18 }
    ];

    XLSX.writeFile(workbook, `entradas_estoque_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
    toast.success("Relatório Excel exportado com sucesso!");
  };

  // Export to PDF
  const exportToPDF = () => {
    if (entries.length === 0) {
      toast.error("Sem dados para exportar");
      return;
    }

    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(18);
    doc.setTextColor(31, 41, 55);
    doc.text("Histórico de Entradas de Estoque", 14, 22);
    
    // Date
    doc.setFontSize(10);
    doc.setTextColor(107, 114, 128);
    doc.text(`Gerado em: ${format(new Date(), "dd/MM/yyyy HH:mm")}`, 14, 30);

    // Table
    const tableData = entries.map(entry => [
      entry.product_name,
      entry.supplier_name,
      formatDate(entry.expiration_date),
      entry.quantity.toString(),
      formatCurrency(entry.unit_cost),
      formatDateTime(entry.created_at),
    ]);

    autoTable(doc, {
      head: [["Produto", "Fornecedor", "Validade", "Qtd", "Custo Unit.", "Data"]],
      body: tableData,
      startY: 38,
      styles: { 
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: { 
        fillColor: [16, 185, 129],
        textColor: 255,
        fontStyle: "bold"
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: 14, right: 14 },
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(156, 163, 175);
      doc.text(
        `Página ${i} de ${pageCount} - EstoqueClinic`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: "center" }
      );
    }

    doc.save(`entradas_estoque_${format(new Date(), "yyyy-MM-dd")}.pdf`);
    toast.success("Relatório PDF exportado com sucesso!");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="stock-entry-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
          Entrada de Estoque
        </h1>
        <p className="text-slate-500 mt-1">
          Registre a entrada de novos produtos no estoque
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Entry Form */}
        <div className="lg:col-span-1">
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <ArrowDownToLine className="h-5 w-5 text-emerald-600" />
                Nova Entrada
              </CardTitle>
              <CardDescription>
                Preencha os dados da entrada de estoque
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="product">Produto *</Label>
                  <Select
                    value={formData.product_id}
                    onValueChange={(value) => setFormData({ ...formData, product_id: value })}
                  >
                    <SelectTrigger data-testid="entry-product-select">
                      <SelectValue placeholder="Selecione o produto" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.filter(p => p.id && p.name).map((product) => (
                        <SelectItem key={product.id} value={product.id}>
                          <div className="flex items-center gap-2">
                            <Package className="h-4 w-4" />
                            {product.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="supplier">Fornecedor *</Label>
                  <Select
                    value={formData.supplier_id}
                    onValueChange={(value) => setFormData({ ...formData, supplier_id: value })}
                  >
                    <SelectTrigger data-testid="entry-supplier-select">
                      <SelectValue placeholder="Selecione o fornecedor" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.filter(s => s.id && s.name).map((supplier) => (
                        <SelectItem key={supplier.id} value={supplier.id}>
                          <div className="flex items-center gap-2">
                            <Truck className="h-4 w-4" />
                            {supplier.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Data de Validade *</Label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      placeholder="DD/MM/AAAA"
                      value={expirationDateText}
                      onChange={(e) => handleDateTextChange(e.target.value, setExpirationDate, setExpirationDateText)}
                      className="flex-1"
                      data-testid="entry-expiration-input"
                    />
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          type="button"
                          data-testid="entry-expiration-btn"
                        >
                          <CalendarIcon className="h-4 w-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="end">
                        <Calendar
                          mode="single"
                          selected={expirationDate}
                          onSelect={(date) => handleCalendarSelect(date, setExpirationDate, setExpirationDateText)}
                          locale={ptBR}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="quantity">Quantidade *</Label>
                    <Input
                      id="quantity"
                      type="number"
                      min="1"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                      required
                      data-testid="entry-quantity-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="unit_cost">Custo Unitario *</Label>
                    <Input
                      id="unit_cost"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.unit_cost}
                      onChange={(e) => setFormData({ ...formData, unit_cost: e.target.value })}
                      placeholder="0.00"
                      required
                      data-testid="entry-cost-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="entry_date">Data da Entrada</Label>
                  <Input
                    id="entry_date"
                    type="date"
                    value={formData.entry_date}
                    onChange={(e) => setFormData({ ...formData, entry_date: e.target.value })}
                    data-testid="entry-date-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={submitting || !hasPermission("add_entry")}
                  data-testid="entry-submit-btn"
                >
                  {submitting ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                  ) : (
                    <>
                      <Plus className="mr-2 h-4 w-4" />
                      Registrar Entrada
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Recent Entries */}
        <div className="lg:col-span-2">
          <Card className="card-dashboard">
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <History className="h-5 w-5 text-slate-500" />
                    Histórico de Entradas
                  </CardTitle>
                  <CardDescription>
                    Últimas entradas registradas no sistema
                    {isAdmin && " - Clique para editar ou excluir"}
                  </CardDescription>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={entries.length === 0}
                      className="gap-2"
                      data-testid="export-entries-btn"
                    >
                      <Download className="h-4 w-4" />
                      Exportar
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={exportToExcel}>
                      <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
                      Exportar Excel
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportToPDF}>
                      <File className="h-4 w-4 mr-2 text-red-600" />
                      Exportar PDF
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {entries.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                  <ArrowDownToLine className="h-12 w-12 mb-4 text-slate-300" />
                  <p>Nenhuma entrada registrada</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Produto</TableHead>
                        <TableHead>Fornecedor</TableHead>
                        <TableHead>Validade</TableHead>
                        <TableHead className="text-right">Qtd</TableHead>
                        <TableHead className="text-right">Custo Unit.</TableHead>
                        <TableHead>Data</TableHead>
                        {isAdmin && <TableHead className="text-right">Ações</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entries.slice(0, 20).map((entry) => (
                        <TableRow key={entry.id} className="table-row-zebra">
                          <TableCell className="font-medium">
                            {entry.product_name}
                          </TableCell>
                          <TableCell>{entry.supplier_name}</TableCell>
                          <TableCell>{formatDate(entry.expiration_date)}</TableCell>
                          <TableCell className="text-right font-semibold text-emerald-600">
                            +{entry.quantity}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(entry.unit_cost)}
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {formatDateTime(entry.created_at)}
                          </TableCell>
                          {isAdmin && (
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openEditDialog(entry)}
                                  title="Editar"
                                  data-testid={`edit-entry-${entry.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="text-red-500 hover:text-red-700"
                                      title="Excluir"
                                      data-testid={`delete-entry-${entry.id}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Excluir entrada?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Esta ação irá remover a entrada do estoque e ajustar as quantidades.
                                        O registro será movido para a lixeira por 24 horas.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => handleDelete(entry)}
                                        className="bg-red-600 hover:bg-red-700"
                                      >
                                        Excluir
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Editar Entrada de Estoque</DialogTitle>
            <DialogDescription>
              Altere os dados da entrada. As quantidades serão ajustadas automaticamente.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-product">Produto</Label>
                <Select
                  value={editFormData.product_id}
                  onValueChange={(value) => setEditFormData({ ...editFormData, product_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o produto" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.filter(p => p.id && p.name).map((product) => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-supplier">Fornecedor</Label>
                <Select
                  value={editFormData.supplier_id}
                  onValueChange={(value) => setEditFormData({ ...editFormData, supplier_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o fornecedor" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.filter(s => s.id && s.name).map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Data de Validade</Label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="DD/MM/AAAA"
                    value={editExpirationDateText}
                    onChange={(e) => handleDateTextChange(e.target.value, setEditExpirationDate, setEditExpirationDateText)}
                    className="flex-1"
                  />
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        type="button"
                      >
                        <CalendarIcon className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="end">
                      <Calendar
                        mode="single"
                        selected={editExpirationDate}
                        onSelect={(date) => handleCalendarSelect(date, setEditExpirationDate, setEditExpirationDateText)}
                        locale={ptBR}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-quantity">Quantidade</Label>
                  <Input
                    id="edit-quantity"
                    type="number"
                    min="1"
                    value={editFormData.quantity}
                    onChange={(e) => setEditFormData({ ...editFormData, quantity: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-unit_cost">Custo Unitário</Label>
                  <Input
                    id="edit-unit_cost"
                    type="number"
                    step="0.01"
                    min="0"
                    value={editFormData.unit_cost}
                    onChange={(e) => setEditFormData({ ...editFormData, unit_cost: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-entry-date">Data da Entrada</Label>
                <Input
                  id="edit-entry-date"
                  type="date"
                  value={editFormData.entry_date}
                  onChange={(e) => setEditFormData({ ...editFormData, entry_date: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">Salvar Alterações</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockEntryPage;
